import memoize from 'fast-memoize';
import * as React from 'react';
import { memo } from 'react';

import i18n from 'helper/i18n';
import noop from 'helper/noop';

import { isNumber, isString } from '@impuls-coach/util-is';
import { calculateBmi, formatBmi } from 'helper/bmi';
import { template } from 'helper/string';
import { GenderKeyType } from '../../../types/GenderKey';

export type BmiState = {
	currentBmi?: number;
	targetBmi?: number;
	value?: number;
	loadedInitialValue?: boolean;
};

export type BmiProps = {
	size: number;
	weight: number;
	inputValue: string;
	target: Object;
	age: number | null;
	gender: GenderKeyType | null;
	onUpdate: (state: BmiState, props: BmiProps) => void;
};

export const renderStripCaption = (key: string, value?: number): string => {
	const label = i18n(`react.impulsCoach.components.Bmi.${key}`);
	const exactPercantage = (100 / 20) * ((value || 0) - 15);
	let templateValue = formatBmi(parseFloat(value as any));

	if (exactPercantage > 125) {
		templateValue = '> 40';
	} else if (exactPercantage < 5) {
		templateValue = '< 16';
	}

	if (isString(label) && isNumber(value)) {
		return template(
			label,
			{
				bmi: templateValue,
			},
			true
		);
	}

	return '';
};

export const calculatePercentage = memoize((bmi?: number): number => {
	if (bmi) {
		const exactValue = ((bmi - 15) * 100) / 26;

		if (exactValue < 5) {
			return 5;
		} else if (exactValue > 95) {
			return 95;
		}

		return exactValue;
	}

	return -100;
});

export const getAlignPercentageModifier = memoize((bmi: number = 0, position: string): string => {
	if (bmi) {
		const percentage = calculatePercentage(bmi);

		if (percentage >= 90) {
			return ` state-m-bmi__${position}__wrapper--text-alignment-left`;
		} else if (percentage <= 20) {
			return ` state-m-bmi__${position}__wrapper--text-alignment-right`;
		}
	}

	return '';
});

export const BMI_SCALE_SETTINGS = {
	scaleNumbers: [16, 18, 20, 22, 24, 26, 28, 30, 32, 34, 36, 38, 40],
	tiles: [0, 1, 2, 3, 4, 5],
};

export class Bmi extends React.Component<BmiProps, BmiState> {
	public static displayName = 'Bmi';

	public static defaultProps = {
		tiles: [],
		scaleNumbers: [],
		onUpdate: noop,
		inputValue: '0',
	};

	constructor(props: BmiProps) {
		super(props);

		this.state = {};
	}

	public calculateBmiFromCurrentState(props: BmiProps) {
		const currentBmi = calculateBmi(props.weight, props.size, true);
		const targetBmi = calculateBmi(props.weight - parseFloat(props.inputValue), props.size, true);

		this.setState({
			currentBmi,
			targetBmi,
		});
	}

	public updateParentComponent() {
		this.props.onUpdate.apply(this, [this.state, this.props]);
	}

	public componentDidMount() {
		this.calculateBmiFromCurrentState(this.props);
		this.updateParentComponent();
	}

	public componentDidUpdate(prevProps: BmiProps) {
		if (this.props.inputValue && this.props.inputValue !== prevProps.inputValue) {
			this.calculateBmiFromCurrentState(this.props);
		}
	}

	public renderTiles() {
		return BMI_SCALE_SETTINGS.tiles.map((tile: number) => {
			return <li key={tile} className="m-bmi__tile" />;
		});
	}

	public renderScaleNumbers() {
		return BMI_SCALE_SETTINGS.scaleNumbers.map((text: number) => {
			return (
				<li key={text} className="m-bmi__scale__item">
					<span className="m-bmi__scale__text">{text}</span>
				</li>
			);
		});
	}

	public render() {
		return (
			<div className="m-bmi">
				<div className="m-bmi__bar">
					<div className="m-bmi__top">
						<div
							className={`m-bmi__top__wrapper${getAlignPercentageModifier(this.state.currentBmi, 'top')}`}
							style={{ left: `${calculatePercentage(this.state.currentBmi)}%` }}
						>
							<div className="m-bmi__top__pointer" />
							<span className="m-bmi__top__text">
								{renderStripCaption('currentBmiText', this.state.currentBmi)}
							</span>
						</div>
					</div>
					<div className="m-bmi__middle">
						<ol className="m-bmi__tiles">{this.renderTiles()}</ol>
						<ol className="m-bmi__scale">{this.renderScaleNumbers()}</ol>
					</div>
					<div className="m-bmi__bottom">
						<div
							className={`m-bmi__bottom__wrapper${getAlignPercentageModifier(
								this.state.targetBmi,
								'bottom'
							)}`}
							style={{ left: `${calculatePercentage(this.state.targetBmi)}%` }}
						>
							<div className="m-bmi__bottom__pointer" />
							<span className="m-bmi__bottom__text">
								{renderStripCaption('targetBmiText', this.state.targetBmi)}
							</span>
						</div>
					</div>
				</div>
			</div>
		);
	}
}

export default memo(Bmi, (prevProps, nextProps) => {
	return (
		prevProps.inputValue === nextProps.inputValue &&
		prevProps.weight === nextProps.weight &&
		prevProps.size === nextProps.size
	);
});
